// FareCard — Reusable fare-option card used on FlightDetailPage.
// Pure visual; selection state via onClick + selected prop.

import { cn } from '@/lib/cn';
import type { FareTier } from '@/lib/booking-mock';

interface Props {
  fare:     FareTier;
  selected: boolean;
  onClick:  () => void;
}

/** Returns true when a fare attribute value is a "positive" inclusion. */
function isGood(val: string): boolean {
  return (
    !val.startsWith('No ') &&
    !val.startsWith('Not ') &&
    !val.startsWith('Paid') &&
    val !== 'No refund'
  );
}

const FEATURES: Array<{ label: string; key: keyof FareTier }> = [
  { label: 'Checked bag', key: 'bag' },
  { label: 'Changes',     key: 'change' },
  { label: 'Seat',        key: 'seat' },
];

export function FareCard({ fare, selected, onClick }: Props) {
  const tagColor = fare.tag === 'POPULAR' ? '#FF5A3C' : '#F5A623';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onClick()}
      className={cn(
        'relative rounded-[18px] p-[18px] cursor-pointer border-2',
        'transition-all duration-200 select-none outline-none',
        'focus-visible:ring-2 focus-visible:ring-[#FF5A3C]/60',
        selected
          ? 'border-[#FF5A3C] bg-[#FF5A3C]/10'
          : 'border-white/[0.07] bg-[#131C30] hover:border-white/[0.18]',
      )}
    >
      {/* Tag badge */}
      {fare.tag && (
        <div
          className="absolute -top-[11px] left-1/2 -translate-x-1/2 px-2.5 py-[3px] rounded-full text-[9px] font-bold tracking-[0.5px] text-white whitespace-nowrap"
          style={{ background: tagColor }}
        >
          {fare.tag}
        </div>
      )}

      {/* Fare name */}
      <div className="text-[15px] font-bold text-[#F4F3F0] mb-1.5"
           style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}>
        {fare.label}
      </div>

      {/* Price */}
      <div
        className={cn(
          'text-[24px] font-bold tracking-tight leading-none mb-0.5 transition-colors duration-200',
          selected ? 'text-[#FF5A3C]' : 'text-[#F4F3F0]',
        )}
        style={{ fontFamily: '"Fraunces Variable","Fraunces",Georgia,serif' }}
      >
        {fare.priceDisplay}
      </div>
      <div className="text-[11px] text-[#8C98B0] mb-3"
           style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif' }}>
        per person
      </div>

      {/* Feature list */}
      <div className="flex flex-col gap-2">
        {FEATURES.map(({ label, key }) => {
          const val  = fare[key] as string;
          const good = isGood(val);
          return (
            <div key={label} className="flex items-start gap-1.5">
              <span
                className="text-[12px] font-bold leading-[1.4] flex-shrink-0"
                style={{ color: good ? '#FF5A3C' : '#4E5E78' }}
              >
                {good ? '✓' : '—'}
              </span>
              <span
                className="text-[11.5px] leading-[1.4]"
                style={{
                  fontFamily: '"Inter Variable","Inter",system-ui,sans-serif',
                  color: good ? '#F4F3F0' : '#8C98B0',
                }}
              >
                {val}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
